'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import TorrentPlayer from './TorrentPlayer';

export type PlayerSource = { label: string; url: string };

function normalizeSources(raw: unknown): PlayerSource[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s, i): PlayerSource | null => {
      if (typeof s === 'string') return { label: `Source ${i + 1}`, url: s };
      if (s && typeof s === 'object' && typeof (s as any).url === 'string') {
        return { label: String((s as any).label || `Source ${i + 1}`), url: (s as any).url };
      }
      return null;
    })
    .filter((s): s is PlayerSource => !!s && s.url.startsWith('http'));
}

// Player with tabs: Torrent (ad-free, default) + all embed servers (SUB/DUB/RAW).
// Embeds are sandboxed WITHOUT allow-popups to block popunder/popup ads.
export default function PlayerBox({
  episodeId,
  initialEmbed,
  initialSources,
  sourceUrl,
  seriesTitle,
  episodeNumber,
  nextHref,
}: {
  episodeId: number;
  initialEmbed: string;
  initialSources: unknown;
  sourceUrl: string;
  seriesTitle: string;
  episodeNumber: number;
  nextHref?: string | null;
}) {
  const [embed, setEmbed] = useState(initialEmbed);
  const [sources, setSources] = useState<PlayerSource[]>(() => normalizeSources(initialSources));
  const [active, setActive] = useState(-1); // -1 = torrent tab (default)
  const [reconnecting, setReconnecting] = useState(false);
  const [trouble, setTrouble] = useState(false);
  const [troubleTimer, setTroubleTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const frameKey = useRef(0);
  const [autoNext, setAutoNext] = useState(true);

  const TORRENT_TAB = -1;
  const current = sources[active]?.url || embed;
  const cacheKey = `${seriesTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-ep-${episodeNumber}`;

  const showTrouble = useCallback(() => {
    setTrouble(true);
  }, []);

  const onFrameLoad = () => {
    if (troubleTimer) clearTimeout(troubleTimer);
    const t = setTimeout(showTrouble, 12000);
    setTroubleTimer(t);
  };

  useEffect(() => {
    return () => {
      if (troubleTimer) clearTimeout(troubleTimer);
    };
  }, [troubleTimer]);

  const reconnect = async () => {
    setReconnecting(true);
    try {
      const res = await fetch('/api/reconnect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ episodeId }),
      });
      const data = await res.json();
      if (data.ok) {
        setEmbed(data.embed || '');
        setSources(normalizeSources(data.sources));
        setActive(sources.length > 0 ? 0 : TORRENT_TAB);
        setTrouble(false);
        frameKey.current += 1; // force iframe remount
      } else {
        setTrouble(true);
      }
    } catch {
      setTrouble(true);
    } finally {
      setReconnecting(false);
    }
  };

  const pick = (i: number) => {
    setActive(i);
    setTrouble(false);
  };

  return (
    <div>
      <div className="relative w-full bg-black rounded-xl overflow-hidden border border-zinc-800">
        <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
          {active === TORRENT_TAB ? (
            <TorrentPlayer
              seriesTitle={seriesTitle}
              episodeNumber={episodeNumber}
              cacheKey={cacheKey}
            />
          ) : current ? (
            <iframe
              key={`${frameKey.current}-${active}`}
              src={current}
              className="player-frame"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              referrerPolicy="no-referrer"
              onLoad={onFrameLoad}
              sandbox="allow-scripts allow-same-origin allow-presentation allow-forms"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400 gap-3 p-6 text-center">
              <p className="text-lg font-semibold">Player unavailable</p>
              <p className="text-sm max-w-md">
                No torrents or embeds found for this episode. Try reconnecting or the source page.
              </p>
              <div className="flex gap-2">
                <button onClick={reconnect} className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm">
                  Reconnect player
                </button>
                <a href={sourceUrl} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-lg bg-zinc-800 text-white text-sm">
                  Open source page ↗
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* source tabs — Torrent (ad-free) + SUB/DUB/RAW x servers */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        <button
          onClick={() => pick(TORRENT_TAB)}
          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
            active === TORRENT_TAB
              ? 'bg-emerald-600/25 text-emerald-200 border-emerald-600/60'
              : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-emerald-500'
          }`}
        >
          ⚡ Torrent (ad-free)
        </button>
        {sources.map((s, i) => (
          <button
            key={`${s.url}-${i}`}
            onClick={() => pick(i)}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
              i === active
                ? 'bg-violet-600/25 text-violet-200 border-violet-600/60'
                : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-violet-500'
            }`}
          >
            {s.label}
          </button>
        ))}
        {nextHref && (
          <label className="ml-auto flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoNext}
              onChange={(e) => setAutoNext(e.target.checked)}
              className="accent-violet-500"
            />
            Auto-next
          </label>
        )}
      </div>

      {/* troubleshoot bar */}
      {(trouble || reconnecting) && (
        <div className="mt-2 rounded-lg border border-amber-600/40 bg-amber-600/10 p-3 text-sm text-amber-200">
          {reconnecting ? (
            <p>Reconnecting to the player…</p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <span>Player not working? The source may have rotated — try:</span>
              <button onClick={reconnect} className="px-3 py-1 rounded-md bg-amber-600/30 hover:bg-amber-600/50 text-amber-100">
                Reconnect player
              </button>
              <a href={sourceUrl} target="_blank" rel="noreferrer" className="px-3 py-1 rounded-md bg-zinc-800 text-zinc-200">
                Open source page ↗
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
